import { CurrencyInput, DateInput, Input, Select } from '@payaca/ambrosia';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import './RecordPaymentControl.sass';

import { useSelector } from '@/api/state';
import { getDeal, getInvoice } from '@/utils/stateAccessors';
import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import Checkbox from '@payaca/components/checkbox/Checkbox';
import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';
import FieldLabel from '@payaca/components/fieldLabel/FieldLabel';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';

import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import {
  getAllowEmptyValidator,
  getDateRangeFieldValidator,
  getIsRequiredFieldValidator,
  getLengthFieldValidator,
  getNumericalRangeFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import * as jobPaymentsActions from '@payaca/store/jobPayments/jobPaymentsActions';
import { RecordJobPaymentRequestData } from '@payaca/store/jobPayments/jobPaymentsTypes';
import { AccountRegions } from '@payaca/types/accountTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { PaymentMethodType } from '@payaca/types/jobPaymentTypes';
import UntitledIcon from '@payaca/untitled-icons';
import useGetMyRegionPreferences from '../../../api/queries/me/useGetMyRegionPreferences';

const paymentMethodOptions = [
  {
    label: `Bank transfer`,
    value: PaymentMethodType.BACS,
  },
  {
    label: 'Cash',
    value: PaymentMethodType.CASH,
  },
  {
    label: 'Cheque',
    value: PaymentMethodType.CHEQUE,
  },
  {
    label: 'Card',
    value: PaymentMethodType.CARD,
  },
  {
    label: 'Other',
    value: PaymentMethodType.OTHER,
  },
];

const getFieldValidators = (
  maxPaymentValue: number,
  region: AccountRegions
) => {
  const maxDate = new Date();
  maxDate.setMinutes(maxDate.getMinutes() + 10);

  const timestampDateRegionalFormat = getInternationalMomentDateFormatByRegion(
    DateFormats.TIMESTAMP,
    region
  );

  return {
    paymentValue: [
      getIsRequiredFieldValidator({ readableName: 'payment amount' }),
      getNumericalRangeFieldValidator(
        0,
        maxPaymentValue + 1,
        'payment amount',
        'payment amount must be greater than zero',
        `payment amount must not exceed ${currencyPrice(
          maxPaymentValue,
          region
        )}`
      ),
    ],
    paymentMethod: [
      getIsRequiredFieldValidator({ readableName: 'payment method' }),
    ],
    paymentDate: [
      getAllowEmptyValidator(
        getDateRangeFieldValidator(
          undefined,
          maxDate,
          undefined,
          timestampDateRegionalFormat
        )
      ),
    ],
    note: [getLengthFieldValidator({ max: 150 })],
  };
};

type Props = {
  outstandingPaymentValue: number;
  isDepositPayment: boolean;
  customerName: string;
  dealId: number;
  invoiceId?: number;
  canSendReceipt?: boolean;
  maxPaymentValue?: number;
  recordPaymentCallback?: () => void;
};

const RecordPaymentControl: FunctionComponent<Props> = ({
  outstandingPaymentValue,
  isDepositPayment,
  customerName,
  dealId,
  invoiceId,
  canSendReceipt = true,
  maxPaymentValue,
  recordPaymentCallback,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasCalledCallback, setHasCalledCallback] = useState(false);
  const isRecordingJobPayment = useSelector((state) => {
    return state.jobPayments.isRecordingJobPayment;
  });
  const isJobPaymentRecordedSuccessfully = useSelector((state) => {
    return state.jobPayments.isJobPaymentRecordedSuccessfully;
  });

  const isOutstandingPaymentDue = useMemo(() => {
    return !!outstandingPaymentValue;
  }, [outstandingPaymentValue]);

  const deal = useSelector((state) => {
    return getDeal(state, dealId);
  });

  const invoice = useSelector((state) => {
    if (!invoiceId) return;
    return getInvoice(state, invoiceId);
  });

  const region = useSelector(
    (state: any) => state.users.myProfile.accounts[0].region
  );

  const { data: regionPreferences } = useGetMyRegionPreferences();

  useEffect(() => {
    if (
      isSubmitted &&
      !isRecordingJobPayment &&
      isJobPaymentRecordedSuccessfully &&
      recordPaymentCallback &&
      !hasCalledCallback
    ) {
      setHasCalledCallback(true);
      recordPaymentCallback();
    }
  }, [
    isSubmitted,
    isRecordingJobPayment,
    isJobPaymentRecordedSuccessfully,
    hasCalledCallback,
    recordPaymentCallback,
  ]);

  const onSubmit = useCallback(
    (recordJobPaymentRequestData: RecordJobPaymentRequestData) => {
      dispatch(
        jobPaymentsActions.requestRecordJobPayment(recordJobPaymentRequestData)
      );
      setIsSubmitted(true);
    },
    [dispatch]
  );

  const paymentTarget = useMemo(() => {
    if (invoice)
      return `invoice #${invoice.customReference || invoice.reference}`;
    if (deal) return `Project #${deal.customReference || deal.reference}`;
    return 'this Project';
  }, [deal, invoice]);

  const fieldValidators = useMemo(() => {
    return getFieldValidators(
      maxPaymentValue === undefined ? 10000000 : maxPaymentValue,
      region
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'region'
  }, [maxPaymentValue]);

  const canRecordPayment = useMemo(() => {
    return maxPaymentValue !== 0;
  }, [maxPaymentValue]);

  const shortDateRegionalFormat = useMemo(
    () => getInternationalMomentDateFormatByRegion(DateFormats.SHORT, region),
    [region]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      const paymentValueExceedsOutstanding =
        formState.paymentValue > outstandingPaymentValue;

      return (
        <React.Fragment>
          <div className="fields-container">
            <div className="flex-container">
              <div>
                <ValidatedFieldWrapper
                  validationResult={validationState.paymentValue}
                  isTouched={touchedState.paymentValue || false}
                >
                  <FieldLabel label="Payment amount" isRequired />
                  <CurrencyInput
                    value={formState.paymentValue || 0}
                    onChange={(change) =>
                      onFieldChange({ paymentValue: change })
                    }
                    currency={regionPreferences?.currency}
                  />
                </ValidatedFieldWrapper>
              </div>
              <div>
                <ValidatedFieldWrapper
                  validationResult={validationState.paymentMethod}
                  isTouched={touchedState.paymentMethod || false}
                >
                  <FieldLabel label="Payment method" isRequired />
                  <Select
                    options={paymentMethodOptions}
                    value={formState.paymentMethod || PaymentMethodType.BACS}
                    onChange={(change) => {
                      onFieldTouch('paymentMethod');
                      onFieldChange({ paymentMethod: change });
                    }}
                  />
                </ValidatedFieldWrapper>
              </div>
              <div>
                <ValidatedFieldWrapper
                  validationResult={validationState.paymentDate}
                  isTouched={touchedState.paymentDate || false}
                >
                  <div>
                    <FieldLabel label="Payment date" />
                    <DateInput
                      value={
                        formState.paymentDate
                          ? new Date(formState.paymentDate)
                          : undefined
                      }
                      onChange={(change) =>
                        onFieldChange({ paymentDate: change })
                      }
                      config={{
                        dates: {
                          maxDate: new Date(),
                        },
                      }}
                    />
                  </div>
                </ValidatedFieldWrapper>
              </div>
            </div>
            <ValidatedFieldWrapper
              validationResult={validationState.note}
              isTouched={touchedState.note || false}
            >
              <FieldLabel label="Internal note" />
              <Input
                name="note"
                value={formState.note}
                onChange={(value) => onFieldChange({ note: value })}
                onTouch={() => onFieldTouch('note')}
              />
            </ValidatedFieldWrapper>
          </div>

          {canSendReceipt && (
            <div>
              <div className="checkbox-container flex-container flex-center">
                <div>
                  <Checkbox
                    isChecked={
                      formState.sendReceipt === undefined
                        ? true
                        : formState.sendReceipt
                    }
                    onChange={() => {
                      onFieldChange({
                        sendReceipt: !formState.sendReceipt,
                      });
                      onFieldTouch?.('sendReceipt');
                    }}
                  />
                </div>
                <span>
                  I would like to send a receipt to the customer for this amount
                </span>
              </div>
            </div>
          )}
          <p>
            By continuing I confirm that I have received this{' '}
            {isDepositPayment ? 'deposit payment' : 'payment'} from{' '}
            {customerName} towards {paymentTarget}.
          </p>

          <div className="actions-container">
            {paymentValueExceedsOutstanding && isOutstandingPaymentDue && (
              <ErrorMessage
                message={`A payment of ${currencyPrice(
                  formState.paymentValue,
                  region
                )} exceeds the
              outstanding amount of ${currencyPrice(
                outstandingPaymentValue,
                region
              )}.
              Are you sure you want to record this payment?`}
              />
            )}
            {!isOutstandingPaymentDue && (
              <ErrorMessage
                message="There is no outstanding payment required against this Project. Are
              you sure you want to record a payment?"
              />
            )}
            <Button
              onClick={() => onSubmit(formState as RecordJobPaymentRequestData)}
              isDisabled={!isValid}
              styleVariant={ButtonStyleVariant.OUTSIZE}
            >
              Continue
            </Button>
          </div>
        </React.Fragment>
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'regionPreferences.currency'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: shortDateRegionalFormat
    [
      onSubmit,
      customerName,
      paymentTarget,
      isDepositPayment,
      outstandingPaymentValue,
      canSendReceipt,
      isOutstandingPaymentDue,
      region,
      shortDateRegionalFormat,
    ]
  );

  const initialFormState = useMemo(() => {
    return {
      paymentValue: outstandingPaymentValue,
      paymentMethod: PaymentMethodType.BACS,
      isDepositPayment: isDepositPayment,
      sendReceipt: canSendReceipt,
      dealId: dealId,
      invoiceId: invoiceId,
      paymentDate: new Date(),
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'dealId'
  }, [outstandingPaymentValue, isDepositPayment, canSendReceipt, invoiceId]);

  return (
    <div className="record-payment-control">
      {!isSubmitted && canRecordPayment && (
        <ValidatedForm
          renderFormContents={renderFormContents}
          initialFormState={initialFormState}
          fieldValidators={fieldValidators}
        />
      )}
      {!isSubmitted && !canRecordPayment && (
        <p style={{ textAlign: 'center' }}>
          Unable to record a payment at this time.
        </p>
      )}
      {isSubmitted && isRecordingJobPayment && (
        <div className="loader-container">
          <MiniLoader />
        </div>
      )}

      {isSubmitted && !isRecordingJobPayment && (
        <div className="feedback-container">
          {isJobPaymentRecordedSuccessfully ? (
            <React.Fragment>
              <UntitledIcon name="check" />
              <p>Payment has been successfully recorded!</p>
            </React.Fragment>
          ) : (
            <p>Something went wrong recording this payment</p>
          )}
        </div>
      )}
    </div>
  );
};

export default RecordPaymentControl;
